// TODO : Add UI Testcases for  Ms Teams Connection Project Settings : https://jdog.jira-dev.com/browse/CHAT-423
import React, { useEffect, useRef } from 'react';
import queryString from 'query-string';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { AppNameType, ICommunicationConfig, TsiteConnectionData } from '../../../common/types';
import { CommunicationCard } from '../../../common/ui/communication-card';
import { communicationConfig } from '../../../common/ui/communication-card/constants';
import {
	useMsTeamsConnectionStatusInfo,
	useMsTeamsWorkspaceConnections,
	useZoomWorkspaceConnections,
	useZoomConnectionStatusInfo,
} from '../../../services/main.tsx';

export const WorkspaceCommunicationConnection = () => {
	const ref = useRef<HTMLDivElement>(null);
	const tenantContext = useTenantContext();
	const { isAdmin } = tenantContext;
	const { data: projectContext } = useProjectContext();
	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;

	const [{ msTeamsWorkspaceConnectionsData, isLoading: isMsTeamsLoading }] =
		useMsTeamsWorkspaceConnections();
	const [{ zoomWorkspaceConnectionsData, isLoading: isZoomLoading }] =
		useZoomWorkspaceConnections();

	const [
		{ isMsTeamsEnabled, isConnected, isChangeInStatusProgress },
		{ setMsTeamsWorkspaceConnectionStateForProject, connectMsTeamstoProject },
	] = useMsTeamsConnectionStatusInfo();
	const [
		{
			isZoomEnabled,
			isConnected: isZoomConnectedStatus,
			isChangeInStatusProgress: isZoomChangeInStatusProgress,
		},
		{ setZoomWorkspaceConnectionStateForProject, isZoomConnected, connectZoomToProject },
	] = useZoomConnectionStatusInfo();

	useEffect(() => {
		const { search } = window.location;
		const { showIMSetting } = queryString.parse(search);
		if (ref?.current !== undefined && ref?.current !== null && showIMSetting === 'true') {
			scrollIntoViewIfNeeded(ref?.current, {
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest',
			});
		}
	}, [ref]);

	useEffect(() => {
		if (zoomWorkspaceConnectionsData) {
			isZoomConnected({
				connectionId: zoomWorkspaceConnectionsData.id,
				projectId: projectId ?? '',
			});
		}
	}, [zoomWorkspaceConnectionsData, projectId, isZoomConnected]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const toggleHandler = (appName: any, event: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		switch (appName) {
			case 'Microsoft Teams':
				if (!msTeamsWorkspaceConnectionsData || !msTeamsWorkspaceConnectionsData[0]) {
					return;
				}
				if (isConnected) {
					setMsTeamsWorkspaceConnectionStateForProject({
						connectionId: msTeamsWorkspaceConnectionsData[0]?.id ?? '',
						projectId: projectId ?? '',
						enabled: !isMsTeamsEnabled,
					});
					fireUIAnalytics(analyticsEvent, 'toggle clicked', 'enableDisableJsmProjectToTeams', {
						toggleTo: !isMsTeamsEnabled ? 'ENABLED' : 'DISABLED',
						containerType: 'project',
						containerId: projectId ?? '',
						source: 'incidentManagementSettingsScreen',
					});
				} else {
					connectMsTeamstoProject({
						connectionId: msTeamsWorkspaceConnectionsData[0].id,
						projectId: projectId ?? '',
					});
					fireUIAnalytics(analyticsEvent, 'toggle clicked', 'connectJsmProjectToTeams', {
						toggleTo: 'CONNECTED',
						containerType: 'project',
						containerId: projectId ?? '',
						source: 'incidentManagementSettingsScreen',
					});
				}
				break;
			case 'Zoom':
				if (isZoomConnectedStatus) {
					setZoomWorkspaceConnectionStateForProject({
						connectionId: zoomWorkspaceConnectionsData?.id ?? '',
						projectId: projectId ?? '',
						enabled: !isZoomEnabled,
					});
					fireUIAnalytics(analyticsEvent, 'toggle clicked', 'enableDisableJsmProjectToZoom', {
						toggleTo: !isZoomEnabled ? 'ENABLED' : 'DISABLED',
						containerType: 'project',
						containerId: projectId ?? '',
						source: 'incidentManagementSettingsScreen',
					});
				} else {
					connectZoomToProject({
						connectionId: zoomWorkspaceConnectionsData?.id ?? '',
						projectId: projectId ?? '',
					});
					fireUIAnalytics(analyticsEvent, 'toggle clicked', 'connectJsmProjectToZoom', {
						toggleTo: 'CONNECTED',
						containerType: 'project',
						containerId: projectId ?? '',
						source: 'incidentManagementSettingsScreen',
					});
				}
				break;

			default:
				break;
		}
	};

	const getCommuncationStatus = (
		appName: AppNameType,
	): { isCommunicationEnabled: boolean | undefined; isChangeInStatusProgress: boolean } => {
		switch (appName) {
			case 'Microsoft Teams':
				return {
					isCommunicationEnabled: isMsTeamsEnabled,
					isChangeInStatusProgress,
				};
			case 'Zoom':
				return {
					isCommunicationEnabled: isZoomEnabled,
					isChangeInStatusProgress: isZoomChangeInStatusProgress,
				};

			default:
				return { isCommunicationEnabled: false, isChangeInStatusProgress: false };
		}
	};

	const getSiteConnectionData = (appName: AppNameType): TsiteConnectionData | undefined => {
		switch (appName) {
			case 'Microsoft Teams':
				return msTeamsWorkspaceConnectionsData ? msTeamsWorkspaceConnectionsData[0] : undefined;
			case 'Zoom':
				return zoomWorkspaceConnectionsData;

			default:
				return undefined;
		}
	};

	const getLoadingStatus = (appName: AppNameType) => {
		switch (appName) {
			case 'Microsoft Teams':
				return isMsTeamsLoading;
			case 'Zoom':
				return isZoomLoading;

			default:
				return true;
		}
	};

	return (
		<div ref={ref}>
			{communicationConfig.map((config: ICommunicationConfig) => {
				if (config.appName === 'Microsoft Teams') {
					return;
				}
				return (
					<CommunicationCard
						key={config.appName}
						config={config}
						isLoading={getLoadingStatus(config.appName)}
						siteConnectionData={getSiteConnectionData(config.appName)}
						isSiteAdmin={isAdmin}
						isCommunicationEnabled={getCommuncationStatus(config.appName).isCommunicationEnabled}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						onToggleChangeHandler={(...args: [event: any, analyticsEvent: any]) =>
							toggleHandler(config.appName, ...args)
						}
						toggleDisabled={getCommuncationStatus(config.appName).isChangeInStatusProgress}
					/>
				);
			})}
		</div>
	);
};
